/**
 * FeedbackHub browser client — vendored into each client repo (contract D-16).
 *
 * Source of truth: FeedbackHub/clients/web/feedback-client.ts
 * CONTRACT_VERSION: 1
 *
 * localStorage keys (document in each project's CLAUDE.md / component header):
 *   feedbackQueue:v1  — offline / failed-submit retry queue (cap 20, drop after 7 days)
 *
 * Copy this file into the client as-is; do not publish to npm.
 */

export const CONTRACT_VERSION = 1;

/** Build stamp from Vite `define` (Task 1.2). Referenced so the SHA is present in the bundle. */
export const APP_VERSION =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";

export type IncidentType = "bug" | "feature" | "contact" | "rating" | "nps";
export type Rating = "up" | "down";
export type Severity = "low" | "normal" | "high";

export interface FeedbackContext {
  version: 1;
  appId: string;
  appVersion: string;
  surfaceId: string;
  route: string;
  occurredAt: string;
  timezone: string;
  locale: string;
  viewport: string;
  deviceClass: "mobile" | "tablet" | "desktop";
  browser: string;
  os: string;
  prefersColorScheme: "light" | "dark";
  sessionRef?: string;
  payload?: Record<string, unknown>;
}

export interface SubmitIncidentInput {
  appId: string;
  type: IncidentType;
  rating?: Rating;
  category?: string;
  severity?: Severity;
  title?: string;
  body: string;
  contactEmail?: string;
  contactName?: string;
  context: FeedbackContext;
  /** Honeypot — must be empty for a real submission. */
  hp?: string;
  /** Milliseconds from form/control open to submit; server requires >= 3000. */
  elapsedMs: number;
  captchaToken?: string;
}

export interface SubmitIncidentSuccess {
  ok: true;
  ref: string;
  statusUrl: string;
  deduped: boolean;
  /** True when the request was queued locally (network / 5xx / offline). */
  queued: boolean;
}

export interface SubmitIncidentFailure {
  ok: false;
  error: string;
  /** True when a 4xx validation error was returned (not queued). */
  validation: boolean;
}

export type SubmitIncidentResult = SubmitIncidentSuccess | SubmitIncidentFailure;

const QUEUE_KEY = "feedbackQueue:v1";
const QUEUE_CAP = 20;
const QUEUE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_ENDPOINT = "/api/incidents";

/** Page-load epoch used when a caller does not pass an explicit open timestamp. */
const PAGE_OPENED_AT = typeof Date !== "undefined" ? Date.now() : 0;

export function getElapsedMs(openedAt?: number): number {
  return Date.now() - (openedAt ?? PAGE_OPENED_AT);
}

interface QueuedItem {
  payload: SubmitIncidentInput;
  enqueuedAt: number;
}

function readQueue(): QueuedItem[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is QueuedItem =>
        typeof item === "object" &&
        item !== null &&
        typeof (item as QueuedItem).enqueuedAt === "number" &&
        typeof (item as QueuedItem).payload === "object",
    );
  } catch {
    return [];
  }
}

function writeQueue(items: QueuedItem[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  } catch {
    // Quota or private mode — drop silently; the user already saw success.
  }
}

function pruneQueue(items: QueuedItem[]): QueuedItem[] {
  const now = Date.now();
  const fresh = items.filter((item) => now - item.enqueuedAt < QUEUE_MAX_AGE_MS);
  if (fresh.length > QUEUE_CAP) return fresh.slice(fresh.length - QUEUE_CAP);
  return fresh;
}

function enqueue(payload: SubmitIncidentInput): void {
  const next = pruneQueue(readQueue());
  next.push({ payload, enqueuedAt: Date.now() });
  writeQueue(pruneQueue(next));
}

async function postIncident(
  endpoint: string,
  payload: SubmitIncidentInput,
): Promise<SubmitIncidentResult> {
  const res = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      appId: payload.appId,
      type: payload.type,
      ...(payload.rating ? { rating: payload.rating } : {}),
      ...(payload.category ? { category: payload.category } : {}),
      ...(payload.severity ? { severity: payload.severity } : {}),
      ...(payload.title ? { title: payload.title } : {}),
      body: payload.body,
      ...(payload.contactEmail ? { contactEmail: payload.contactEmail } : {}),
      ...(payload.contactName ? { contactName: payload.contactName } : {}),
      context: payload.context,
      hp: payload.hp ?? "",
      elapsedMs: payload.elapsedMs,
      ...(payload.captchaToken ? { captchaToken: payload.captchaToken } : {}),
      contractVersion: CONTRACT_VERSION,
    }),
  });

  if (res.ok) {
    const data = (await res.json()) as {
      ref?: string;
      statusUrl?: string;
      deduped?: boolean;
    };
    if (typeof data.ref === "string" && typeof data.statusUrl === "string") {
      return {
        ok: true,
        ref: data.ref,
        statusUrl: data.statusUrl,
        deduped: Boolean(data.deduped),
        queued: false,
      };
    }
    return { ok: false, error: "Unexpected response from server.", validation: false };
  }

  const status = res.status;
  let message = "Something went wrong. Please try again.";
  try {
    const data = (await res.json()) as { error?: string };
    if (data.error) message = data.error;
  } catch {
    // keep generic
  }

  if (status >= 400 && status < 500 && status !== 429) {
    return { ok: false, error: message, validation: true };
  }

  // Network-ish / rate-limit / 5xx — caller decides whether to queue.
  return { ok: false, error: message, validation: false };
}

export interface SubmitIncidentOptions {
  /** Defaults to same-origin `/api/incidents`. */
  endpoint?: string;
  /**
   * When true (default), non-validation failures are queued and the caller
   * receives a synthetic success so the UI can say "Thanks!".
   */
  queueOnFailure?: boolean;
}

/**
 * Submit an incident to the hub. On network / 5xx / 429 failure the payload is
 * queued in `feedbackQueue:v1` and a queued success is returned (UI still says
 * thanks). Validation 4xx is returned as a real failure so the user can fix it.
 */
export async function submitIncident(
  input: SubmitIncidentInput,
  options: SubmitIncidentOptions = {},
): Promise<SubmitIncidentResult> {
  const endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
  const queueOnFailure = options.queueOnFailure !== false;

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    if (queueOnFailure) {
      enqueue(input);
      return {
        ok: true,
        ref: "QUEUED",
        statusUrl: "",
        deduped: false,
        queued: true,
      };
    }
    return { ok: false, error: "You appear to be offline.", validation: false };
  }

  try {
    const result = await postIncident(endpoint, input);
    if (result.ok) return result;
    if (!result.validation && queueOnFailure) {
      enqueue(input);
      return {
        ok: true,
        ref: "QUEUED",
        statusUrl: "",
        deduped: false,
        queued: true,
      };
    }
    return result;
  } catch {
    if (queueOnFailure) {
      enqueue(input);
      return {
        ok: true,
        ref: "QUEUED",
        statusUrl: "",
        deduped: false,
        queued: true,
      };
    }
    return { ok: false, error: "Network error. Please try again.", validation: false };
  }
}

let flushInFlight = false;

/** Flush queued submissions sequentially. Safe to call repeatedly. */
export async function flushFeedbackQueue(options: SubmitIncidentOptions = {}): Promise<void> {
  if (flushInFlight) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  flushInFlight = true;
  try {
    const endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
    let items = pruneQueue(readQueue());
    writeQueue(items);

    while (items.length > 0) {
      const [head, ...rest] = items;
      try {
        const result = await postIncident(endpoint, head.payload);
        if (result.ok || result.validation) {
          // Drop on success or permanent validation failure (stale payload).
          items = rest;
          writeQueue(items);
          continue;
        }
        // Transient failure — stop and retry later.
        break;
      } catch {
        break;
      }
    }
  } finally {
    flushInFlight = false;
  }
}

let listenersInstalled = false;

/** Install load + online flush listeners once per page. Idempotent. */
export function installFeedbackQueueListeners(options: SubmitIncidentOptions = {}): void {
  if (typeof window === "undefined" || listenersInstalled) return;
  listenersInstalled = true;
  const run = () => {
    void flushFeedbackQueue(options);
  };
  if (document.readyState === "complete") {
    run();
  } else {
    window.addEventListener("load", run, { once: true });
  }
  window.addEventListener("online", run);
}
